/** @format */

import {useState} from 'react';

import {useForm, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {Trans, useTranslation} from 'react-i18next';
import DatePicker, {registerLocale} from 'react-datepicker';
import {ru} from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TodayOutlined from '@mui/icons-material/TodayOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import MailOutlined from '@mui/icons-material/MailOutlined';

import 'react-datepicker/dist/react-datepicker.css';

registerLocale('ru', ru);

import {TextFieldWrap} from 'components/InputsNew/TextFieldWrap';
import {useMixpanelContext} from 'contexts/MixpanelContext';

import {useBookDemoMutation} from '../../graphql/__generated__';
import {validationSchema} from './validationSchema';
import {DEFAULT_VALUES, ID_ROOT, ID_SUBMIT, TIME_OPTIONS} from './constants';
import {FormValues} from './types';
import {
  FormContainer,
  Header,
  HeaderIconWrapper,
  HeaderTitle,
  CloseButton,
  SubtitleText,
  Content,
  Divider,
  FormContent,
  FieldsGrid,
  FullWidthField,
  FieldWrapper,
  FieldLabel,
  FieldHint,
  SubmitButton,
  InputWithIcon,
  InputIcon,
  DatePickerWrapper,
  LightFormControl,
  LightSelect,
  LIGHT_SELECT_MENU_PROPS,
  LightInputWrapper,
  LightMenuItem,
  ErrorText,
} from './styles';

const formatDateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface Props {
  onSuccess: () => void;
  onClose: () => void;
}

const BookDemoForm = ({onSuccess, onClose}: Props) => {
  const {t} = useTranslation();
  const {mixpanel} = useMixpanelContext();

  const [bookDemo, {loading}] = useBookDemoMutation();
  const [hasError, setHasError] = useState(false);

  const handleClose = () => {
    setHasError(false);
    onClose();
  };

  const {
    control,
    handleSubmit,
    formState: {isValid},
  } = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data: FormValues) => {
    setHasError(false);
    try {
      await bookDemo({
        variables: {
          data: {
            demoDate: data.desiredDate ? formatDateToISO(data.desiredDate) : '',
            demoTime: data.desiredTime,
            phone: data.phone,
            email: data.email,
          },
        },
      });

      mixpanel.track('book_demo');

      onSuccess();
    } catch {
      setHasError(true);
    }
  };

  return (
    <FormContainer id={ID_ROOT}>
      <Header>
        <HeaderIconWrapper>
          <VideocamOutlined />
        </HeaderIconWrapper>
        <HeaderTitle>
          <Trans>Онлайн-демо со специалистом</Trans>
        </HeaderTitle>
        <CloseButton disableRipple onClick={handleClose}>
          <CloseIcon />
        </CloseButton>
      </Header>

      <Content>
        <SubtitleText>
          <Trans>Запишитесь на онлайн-демо и мы вам все покажем</Trans>
        </SubtitleText>
        <Divider />

        <FormContent onSubmit={handleSubmit(onSubmit)}>
          <FieldsGrid>
            <FieldWrapper>
              <FieldLabel>
                <Trans>Желаемая дата</Trans>
              </FieldLabel>
              <Controller
                name='desiredDate'
                control={control}
                render={({field}) => (
                  <InputWithIcon>
                    <InputIcon>
                      <TodayOutlined />
                    </InputIcon>
                    <DatePickerWrapper>
                      <DatePicker
                        selected={field.value}
                        onChange={date => field.onChange(date)}
                        placeholderText={t('Выбрать', 'Выбрать')}
                        locale='ru'
                        dateFormat='dd.MM.yyyy'
                        portalId='root'
                        minDate={(() => {
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          return tomorrow;
                        })()}
                      />
                    </DatePickerWrapper>
                  </InputWithIcon>
                )}
              />
              <FieldHint>
                <Trans>Всегда можно передоговориться</Trans>
              </FieldHint>
            </FieldWrapper>

            <FieldWrapper>
              <FieldLabel>
                <Trans>Время проведения</Trans>
              </FieldLabel>
              <Controller
                name='desiredTime'
                control={control}
                render={({field}) => (
                  <InputWithIcon>
                    <InputIcon>
                      <ScheduleOutlined />
                    </InputIcon>
                    <LightFormControl>
                      <LightSelect
                        value={field.value}
                        onChange={e => field.onChange(e.target.value)}
                        IconComponent={ArrowDropDownIcon}
                        MenuProps={LIGHT_SELECT_MENU_PROPS}
                        displayEmpty
                        renderValue={selected => {
                          if (!selected) {
                            return t('Выбрать', 'Выбрать');
                          }
                          return String(selected);
                        }}
                      >
                        {TIME_OPTIONS.map(option => (
                          <LightMenuItem key={option.value} value={option.value}>
                            {option.text}
                          </LightMenuItem>
                        ))}
                      </LightSelect>
                    </LightFormControl>
                  </InputWithIcon>
                )}
              />
              <FieldHint>
                <Trans>Обычно демо занимает от 45 минут до часа</Trans>
              </FieldHint>
            </FieldWrapper>

            <FullWidthField>
              <FieldWrapper>
                <FieldLabel>
                  <Trans>Телефон для связи</Trans>
                </FieldLabel>
                <Controller
                  name='phone'
                  control={control}
                  render={({field}) => (
                    <InputWithIcon>
                      <InputIcon>
                        <PhoneOutlined />
                      </InputIcon>
                      <LightInputWrapper>
                        <TextFieldWrap {...field} type='tel' placeholder={t('+7', '+7')} compact />
                      </LightInputWrapper>
                    </InputWithIcon>
                  )}
                />
                <FieldHint>
                  <Trans>Мы вам позвоним для подтверждения демо</Trans>
                </FieldHint>
              </FieldWrapper>

              <FieldWrapper>
                <FieldLabel>
                  <Trans>Емейл</Trans>
                </FieldLabel>
                <Controller
                  name='email'
                  control={control}
                  render={({field}) => (
                    <InputWithIcon>
                      <InputIcon>
                        <MailOutlined />
                      </InputIcon>
                      <LightInputWrapper>
                        <TextFieldWrap
                          {...field}
                          type='email'
                          placeholder={t('Ввести', 'Ввести')}
                          compact
                        />
                      </LightInputWrapper>
                    </InputWithIcon>
                  )}
                />
                <FieldHint>
                  <Trans>Мы пришлем ссылку на встречу</Trans>
                </FieldHint>
              </FieldWrapper>
            </FullWidthField>
          </FieldsGrid>

          <Divider />
          <SubmitButton id={ID_SUBMIT} type='submit' disabled={!isValid || loading}>
            <Trans>{isValid ? 'Записаться' : 'Заполните поля'}</Trans>
          </SubmitButton>
          {hasError && (
            <ErrorText>
              <Trans>Произошла ошибка, попробуйте ещё раз</Trans>
            </ErrorText>
          )}
        </FormContent>
      </Content>
    </FormContainer>
  );
};

export {BookDemoForm};
